<template>
  <div>
    <el-dialog
      title="Shelf Products"
      :visible.sync="dialogVisible"
      width="1100px"
      custom-class="open-door-line-dialog"
      :modal-append-to-body="true"
      :append-to-body="true"
      :before-close="beforeClose"
      :close-on-click-modal="false"
      @open="open"
      @close="close"
    >
      <div class="product-selector" v-loading="loading" element-loading-text="Loading...">
        <!-- Left Panel: Shelves -->
        <div class="left-panel">
          <div class="panel-header">
            <span class="panel-title">Shelves</span>
            <el-button type="text" size="mini" icon="el-icon-plus" @click="addRow">Add Shelf</el-button>
          </div>
          <el-divider></el-divider>
          <div class="left-panel-scroll">
            <div v-if="!hasRows" class="empty-state">
              <i class="el-icon-files"></i>
              <p>No shelves</p>
              <span>Click "Add Shelf" to start</span>
            </div>
            <div v-else class="shelf-list">
              <el-card
                v-for="(products, rowIndex) in sortedRows"
                :key="rowIndex"
                :class="['shelf-card', { 'is-active': selectedRowIndex == rowIndex }]"
                shadow="hover"
                @click.native="selectRow(rowIndex)"
              >
                <div class="shelf-header">
                  <el-tag size="small" effect="plain">Shelf {{ rowIndex }}</el-tag>
                  <el-button
                    type="text"
                    size="mini"
                    icon="el-icon-delete"
                    class="shelf-delete"
                    @click.stop="handlerCloseRow(rowIndex)"
                  ></el-button>
                </div>
                <div class="shelf-products" v-if="products.length > 0">
                  <div class="product-card" v-for="(product, productIndex) in products" :key="productIndex">
                    <div class="product-header">
                      <span class="product-name" :title="product.productName">{{ product.productName }}</span>
                      <el-tag
                        v-if="isSimilarProductInRows(product)"
                        size="mini"
                        type="warning"
                        class="similar-tag"
                        >{{ $t("productSimilar.hasSimilarGroup") }}</el-tag
                      >
                      <el-button type="text" size="mini" icon="el-icon-close" @click.stop="removeProduct(rowIndex, productIndex)"></el-button>
                    </div>
                    <div class="product-body">
                      <el-image
                        :src="product.productImage"
                        fit="contain"
                        class="product-image"
                      >
                        <div slot="error" class="image-error">
                          <i class="el-icon-picture-outline"></i>
                        </div>
                      </el-image>
                      <div class="product-form">
                        <el-form label-width="70px" size="mini">
                          <el-form-item label="Price">
                            <el-input v-model="product.retailPrice" @input="syncProductPrice(product)"></el-input>
                          </el-form-item>
                          <el-form-item label="Stock">
                            <el-input v-model="product.nowStock"></el-input>
                          </el-form-item>
                          <el-form-item label="Alert">
                            <el-input v-model="product.stockAlarm"></el-input>
                          </el-form-item>
                        </el-form>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="shelf-empty" v-else>
                  <i class="el-icon-goods"></i>
                  <span>Click a product to add</span>
                </div>
              </el-card>
            </div>
          </div>
        </div>

        <!-- Right Panel: Product Library -->
        <div class="right-panel">
          <div class="panel-header">
            <span class="panel-title">Product Library</span>
            <span class="panel-tip">{{ hasRows ? `${rowCount} shelves, ${selectedProductCount} products in current` : 'Add shelf first' }}</span>
          </div>
          <el-divider></el-divider>
          <div class="search-bar">
            <el-input
              v-model="search.productName"
              placeholder="Search products"
              size="mini"
              clearable
              @keyup.enter.native="currentChange(1)"
            >
              <el-button slot="append" icon="el-icon-search" @click="currentChange(1)"></el-button>
            </el-input>
          </div>
          <div class="right-panel-scroll">
            <div v-if="productList.length === 0" class="empty-state">
              <i class="el-icon-search"></i>
              <p>No products found</p>
              <span>Try different keywords</span>
            </div>
            <div v-else class="product-grid">
              <div
                v-for="product in productList"
                :key="product.productId"
                class="library-item"
                @click="addProductToRow($event, product)"
              >
                <el-image :src="product.productImage" fit="contain" class="library-image">
                  <div slot="error" class="image-error">
                    <i class="el-icon-picture-outline"></i>
                  </div>
                </el-image>
                <div class="library-name" :title="product.productName">{{ product.productName }}</div>
                <el-tag
                  v-if="isCandidateSimilarProduct(product)"
                  size="mini"
                  type="warning"
                  class="candidate-similar-tag"
                  >{{ $t("productSimilar.hasSimilarGroup") }}</el-tag
                >
              </div>
            </div>
          </div>
          <el-pagination
            v-if="productList.length > 0"
            layout="prev, pager, next"
            :current-page="page.pageIndex"
            :total="page.totalSize"
            :page-size="page.pageSize"
            :pager-count="5"
            small
            @current-change="currentChange"
          ></el-pagination>
        </div>
      </div>

      <span slot="footer" class="dialog-footer">
        <el-button size="small" @click="handlerCancel">Cancel</el-button>
        <el-button :loading="submitLoading" size="small" type="primary" @click="handlerSubmit">Confirm</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
  export default {
    name: "OpenDoorLine",
    props: {
      show: {
        type: Boolean,
        default: false
      },
      userEquipment: {
        type: Object,
        default: () => ({})
      }
    },
    data() {
      return {
        rows: {},
        selectedRowIndex: null,
        productList: [],
        search: {
          productName: ""
        },
        page: {
          pageSize: 12,
          pageIndex: 1,
          totalSize: 0
        },
        loading: false,
        submitLoading: false,
        startData: ""
      };
    },
    computed: {
      dialogVisible: {
        get() {
          return this.show;
        },
        set(value) {
          if (!value) {
            this.close();
          }
        }
      },
      sortedRows() {
        return Object.keys(this.rows)
          .sort((a, b) => a - b)
          .reduce((acc, key) => {
            acc[key] = this.rows[key];
            return acc;
          }, {});
      },
      hasRows() {
        return Object.keys(this.rows).length > 0;
      },
      rowCount() {
        return Object.keys(this.rows).length;
      },
      selectedProductCount() {
        if (this.selectedRowIndex === null || !this.rows[this.selectedRowIndex]) {
          return 0;
        }
        return this.rows[this.selectedRowIndex].length;
      }
    },
    methods: {
      normalizeProduct(raw) {
        const productId = raw.goodsId || raw.productId || "";
        return {
          ...raw,
          id: raw.id || null,
          productId,
          goodsId: productId,
          productName: raw.goodsName || raw.productName || raw.name || "",
          productImage: raw.url || raw.goodsUrl || raw.image || "",
          retailPrice: raw.sellingPrice || raw.retailPrice || "",
          nowStock: raw.currentInventoryLevel || raw.nowStock || "",
          stockAlarm: raw.inventoryAlertCount || raw.stockAlarm || "",
          netWeight: raw.netWeight || raw.weight || ""
        };
      },
      syncProductPrice(product) {
        const price = product.retailPrice;
        Object.keys(this.rows).forEach(key => {
          this.rows[key].forEach(item => {
            if (item.productId === product.productId) {
              item.retailPrice = price;
            }
          });
        });
      },
      handlerCancel() {
        if (this.rows && JSON.stringify(this.rows) !== this.startData) {
          this.$confirm("You have unsaved changes. Close this dialog anyway?", "Notice", {
            confirmButtonText: "Confirm",
            cancelButtonText: "Cancel"
          })
            .then(() => {
              this.close();
            })
            .catch(() => {});
          return;
        }
        this.close();
      },
      beforeClose(done) {
        if (this.rows && JSON.stringify(this.rows) !== this.startData) {
          this.$confirm("You have unsaved changes. Close this dialog anyway?", "Notice", {
            confirmButtonText: "Confirm",
            cancelButtonText: "Cancel"
          })
            .then(() => done())
            .catch(() => {});
          return;
        }
        done();
      },
      async open() {
        this.rows = {};
        this.selectedRowIndex = null;
        await this.currentChange(1);
        await this.fetchRowConfig();
        if (Object.keys(this.rows).length > 0) {
          this.selectedRowIndex = Object.keys(this.rows)[0];
        }
      },
      fetchRowConfig() {
        return this.$api
          .request(this.$url.OpenDoor.getAisleGoodsConfig, this.$method.get, {
            userEquipmentId: this.userEquipment.id
          })
          .then(res => {
            if (res.code !== 0) {
              return;
            }
            const data = res.data || {};
            const mapRows = {};
            if (Array.isArray(data)) {
              data.forEach(item => {
                const rowKey = item.floorNo || item.aisleNo || 1;
                if (!mapRows[rowKey]) {
                  mapRows[rowKey] = [];
                }
                mapRows[rowKey].push(this.normalizeProduct(item));
              });
            } else {
              Object.keys(data).forEach(rowKey => {
                const rowItems = Array.isArray(data[rowKey]) ? data[rowKey] : [];
                mapRows[rowKey] = rowItems.map(this.normalizeProduct);
              });
            }
            this.rows = mapRows;
            this.startData = JSON.stringify(mapRows);
          });
      },
      handlerSubmit() {
        const openDoorAisleGoodsDTOS = {};
        Object.keys(this.rows).forEach(aisleNo => {
          const products = this.rows[aisleNo].filter(p => p.goodsId || p.productId);
          if (products.length > 0) {
            // Map key 必须是 Integer 类型（后端定义为 Map<Integer, List<...>>）
            const floorNoKey = parseInt(aisleNo, 10);
            openDoorAisleGoodsDTOS[floorNoKey] = products.map((product, index) =>
              this.buildSubmitProduct(product, floorNoKey, index)
            );
          }
        });

        if (Object.keys(openDoorAisleGoodsDTOS).length === 0) {
          this.$message.warning("Please configure at least one product.");
          return;
        }

        const payload = {
          userEquipmentId: String(this.userEquipment.id),
          openDoorAisleGoodsDTOS: openDoorAisleGoodsDTOS
        };

        this.submitLoading = true;
        this.$api
          .request(this.$url.OpenDoor.saveOrEditAisleGoods, this.$method.post, payload)
          .then(res => {
            if (res.code !== 0) {
              this.$message.warning(res.msg || "Save failed.");
              return;
            }
            this.$message.success("Saved successfully.");
            this.$emit("refresh");
            this.startData = JSON.stringify(this.rows);
            this.close();
          })
          .catch(() => {
            this.$message.error("Save failed.");
          })
          .finally(() => {
            this.submitLoading = false;
          });
      },
      buildSubmitProduct(product, floorNo, sort) {
        const item = {
          id: product.id || null,
          goodsId: product.goodsId || product.productId,
          goodsName: product.productName || product.goodsName || product.name || "",
          url: product.productImage || product.goodsUrl || product.url || "",
          sellingPrice: product.retailPrice || product.sellingPrice || 0,
          currentInventoryLevel: product.nowStock || product.currentInventoryLevel || 0,
          inventoryAlertCount: product.stockAlarm || product.inventoryAlertCount || 0,
          floorNo,
          sort
        };
        this.getReadonlySimilarFields().forEach(field => {
          delete item[field];
        });
        return item;
      },
      getReadonlySimilarFields() {
        return [
          "similarGroupId",
          "similarGroupName",
          "similarGroupMemberCount",
          "hasSimilarGroup",
          "hasSimilarInCurrentMachine",
          "similarGroupConfiguredCount",
          "similarToConfiguredProduct"
        ];
      },
      isValidSimilarGroupId(similarGroupId) {
        return similarGroupId !== undefined && similarGroupId !== null && similarGroupId !== "" && similarGroupId !== 0 && similarGroupId !== "0";
      },
      getSimilarGroupKey(similarGroupId) {
        return this.isValidSimilarGroupId(similarGroupId) ? String(similarGroupId) : "";
      },
      isSimilarProductInRows(product) {
        if (product.hasSimilarInCurrentMachine === true) return true;
        return this.hasSameSimilarGroupInRows(product);
      },
      hasSameSimilarGroupInRow(products, product) {
        if (product.hasSimilarGroup !== true) return false;
        const similarGroupKey = this.getSimilarGroupKey(product.similarGroupId);
        if (!similarGroupKey) return false;
        return (products || []).some(
          item =>
            String(item.goodsId || item.productId || "") !== String(product.goodsId || product.productId || "") &&
            item.hasSimilarGroup === true &&
            this.getSimilarGroupKey(item.similarGroupId) === similarGroupKey
        );
      },
      hasSameSimilarGroupInRows(product) {
        return Object.keys(this.rows).some(key => this.hasSameSimilarGroupInRow(this.rows[key], product));
      },
      hasSimilarGroupInRows(product) {
        const similarGroupKey = this.getSimilarGroupKey(product.similarGroupId);
        if (!similarGroupKey) return false;
        return Object.keys(this.rows).some(key =>
          (this.rows[key] || []).some(item => this.getSimilarGroupKey(item.similarGroupId) === similarGroupKey)
        );
      },
      isCandidateSimilarProduct(product) {
        return product.similarToConfiguredProduct || this.hasSimilarGroupInRows(product);
      },
      currentChange(pageIndex) {
        this.page.pageIndex = pageIndex || 1;
        return this.fetchProducts();
      },
      fetchProducts() {
        this.loading = true;
        const params = {
          pageIndex: this.page.pageIndex,
          pageSize: this.page.pageSize,
          goodsName: this.search.productName,
          skuResult: 2
        };
        return this.$api
          .request(this.$url.OpenDoor.getSkuList, this.$method.get, params)
          .then(res => {
            if (res.code === 0 && res.data) {
              const records = res.data.records || [];
              this.productList = records.map(this.normalizeProduct);
              this.page.totalSize = Number(res.data.total || 0);
            }
          })
          .finally(() => {
            this.loading = false;
          });
      },
      close() {
        this.search.productName = "";
        this.$emit("close");
      },
      addRow() {
        const sorted = Object.keys(this.rows)
          .map(Number)
          .sort((a, b) => a - b);
        const newRowKey = sorted.length ? sorted[sorted.length - 1] + 1 : 1;
        this.$set(this.rows, newRowKey, []);
        this.selectedRowIndex = newRowKey;
      },
      handlerCloseRow(index) {
        this.$delete(this.rows, index);
        const adjustedRows = {};
        Object.keys(this.rows).forEach((key, idx) => {
          adjustedRows[idx + 1] = this.rows[key];
        });
        this.rows = adjustedRows;
        if (this.selectedRowIndex === index) {
          this.selectedRowIndex = null;
        }
      },
      selectRow(index) {
        this.selectedRowIndex = index;
      },
      addProductToRow(event, product) {
        if (this.selectedRowIndex === null) {
          this.$message.warning("Please select a shelf first.");
          return;
        }
        const selectedRowProducts = this.rows[this.selectedRowIndex];
        if (!selectedRowProducts.some(item => item.productId === product.productId)) {
          if (this.hasSameSimilarGroupInRows(product)) {
            this.$notify({
              title: this.$t("components.OpenDoorLine.574153-0"),
              message: this.$t("components.OpenDoorLine.855420-1")
            });
          }
          const selectedProduct = JSON.parse(JSON.stringify(product));
          selectedProduct.id = null;
          selectedRowProducts.push(selectedProduct);
          this.animateItem(event, true);
          return;
        }
        this.animateItem(event, false);
      },
      removeProduct(rowIndex, productIndex) {
        this.rows[rowIndex].splice(productIndex, 1);
      },
      animateItem(event, isSuccess) {
        let element = event.target;
        if (element.tagName !== "IMG") {
          const closestProductItem = element.closest(".library-item");
          if (closestProductItem) {
            element = closestProductItem.querySelector("img");
          }
        }
        if (!element) {
          return;
        }
        const animationClass = isSuccess ? "animate__fadeOutLeft" : "animate__shakeX";
        element.classList.add("animate__animated", animationClass);
        element.addEventListener(
          "animationend",
          () => {
            element.classList.remove("animate__animated", animationClass);
          },
          { once: true }
        );
      }
    }
  };
</script>

<style lang="scss" scoped>
.product-selector {
  display: flex;
  gap: 20px;
  height: calc(90vh - 180px);
  min-height: 520px;
}

/* Panel Header */
.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 0 10px;
}
.panel-title {
  font-size: 16px;
  font-weight: 500;
  color: #303133;
}
.panel-tip {
  font-size: 12px;
  color: #909399;
}

.el-divider--horizontal {
  margin: 10px 0;
}

/* Left Panel */
.left-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  border-right: 1px solid #EBEEF5;
  padding-right: 20px;
  min-width: 0;
}

.left-panel-scroll {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

/* Right Panel */
.right-panel {
  width: 320px;
  display: flex;
  flex-direction: column;
  min-width: 320px;
}

.search-bar {
  margin-bottom: 16px;
}

.right-panel-scroll {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: #909399;
  text-align: center;

  i {
    font-size: 48px;
    color: #C0C4CC;
    margin-bottom: 12px;
  }

  p {
    margin: 0 0 4px;
    font-size: 14px;
    color: #606266;
  }

  span {
    font-size: 12px;
  }
}

/* Shelf List */
.shelf-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.shelf-card {
  cursor: pointer;
  transition: border-color 0.2s;

  &.is-active {
    border-color: #409EFF;
  }

  ::v-deep .el-card__body {
    padding: 12px;
  }
}

.shelf-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.shelf-delete {
  color: #F56C6C;
  padding: 0;

  &:hover {
    color: #F56C6C;
  }
}

.shelf-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 80px;
  color: #909399;
  background: #FAFAFA;
  border-radius: 4px;

  i {
    font-size: 24px;
    margin-bottom: 4px;
  }

  span {
    font-size: 12px;
  }
}

/* Product Card in Shelf */
.shelf-products {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 12px;
}

.product-card {
  border: 1px solid #EBEEF5;
  border-radius: 4px;
  padding: 10px;
  background: #FAFAFA;
}

.product-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.product-name {
  font-size: 13px;
  color: #303133;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  margin-right: 8px;
}

.similar-tag {
  flex: 0 0 auto;
  margin-right: 8px;
}

.product-body {
  display: flex;
  gap: 10px;
}

.product-image {
  width: 60px;
  height: 60px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  ::v-deep img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
}

.image-error {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: #F5F7FA;
  color: #C0C4CC;
}

.product-form {
  flex: 1;
  min-width: 0;

  .el-form-item {
    margin-bottom: 4px;
  }

  .el-form-item__label {
    font-size: 12px;
  }

  .el-input__inner {
    padding: 0 8px;
  }
}

/* Product Library Grid */
.product-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
}

.library-item {
  border: 1px solid #EBEEF5;
  border-radius: 4px;
  padding: 8px;
  cursor: pointer;
  transition: border-color 0.2s, background-color 0.2s;
  text-align: center;
  min-width: 0;

  &:hover {
    border-color: #409EFF;
    background: #ECF5FF;
  }
}

.library-image {
  width: 60px;
  height: 60px;
  margin: 0 auto 6px;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}

.library-name {
  font-size: 12px;
  color: #606266;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
}

.candidate-similar-tag {
  display: block;
  width: fit-content;
  margin: 6px auto 0;
}

/* Dialog */
.dialog-footer {
  text-align: right;
}

::v-deep .open-door-line-dialog {
  .el-dialog__header {
    background-color: #409EFF;
    padding: 15px 20px;

    .el-dialog__title {
      color: #fff;
    }

    .el-dialog__headerbtn .el-dialog__close {
      color: #fff;
    }
  }

  .el-dialog__body {
    padding: 20px;
  }
}

/* Pagination */
.el-pagination {
  text-align: center;
  margin-top: 12px;
}
</style>
